import { useState } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";

import BackIcon from "assets/icons/back.svg?react";
import ArrowLeftIcon from "assets/icons/arrow_l.svg?react";
import Input from "components/Input";
import HollowButton from "components/HollowButton";
import Spinner from "components/Spinner";
import ConfigureRadix from "components/ConfigureRadix";
import radixService, { radixQueryKeys } from "api/radixService";
import { ENotificationType, notification } from "utils/notification";
import { radixErrorText } from "modules/radixModule/radixUtils";

import styles from "./styles.module.scss";

// Strona ustawień integracji RADIX (Integracje → karta „RADIX" → Edytuj) — układ jak
// CruSettings: pasek ścieżki z powrotem, stan połączeń tylko do odczytu, „Rozłącz"
// (usuwa wszystkie łańcuchy; miejscowości zostają) i „Edytuj" (formularz pod spodem).

interface Props {
  onCanceled: () => void;
  onSaved: () => void;
  /** Rozłączenie = usunięcie łańcuchów połączeń (obsługiwane tutaj, nie przez IntegrationsTab.handleDisconnect). */
  onDisconnected: () => void;
}

const RadixSettings = ({ onCanceled, onSaved, onDisconnected }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = radixService.useGetSettings();
  const { mutate: save, isLoading: saving } = radixService.useSaveSettings();
  const [editing, setEditing] = useState(false);
  const settings = data?.data;

  if (isLoading || !settings) {
    return <Spinner />;
  }

  const state = (ok: boolean) => (ok ? "skonfigurowane" : "brak");
  const anyConnection = settings.HasWip || settings.HasPogrun || settings.HasGok;

  const disconnect = () => {
    if (!window.confirm("Usunąć zapisane połączenia z bazami RADIX? Zaimportowane paczki zostają.")) return;
    save(
      { WipConnectionString: "", PogrunConnectionString: "", GokConnectionString: "", Places: null },
      {
        onSuccess: (res) => {
          queryClient.setQueryData(radixQueryKeys.settings, res);
          notification({ type: ENotificationType.SUCCESS, title: "RADIX", text: "Usunięto połączenia z bazami RADIX." });
          onDisconnected();
        },
        onError: (e) =>
          notification({ type: ENotificationType.ERROR, title: "RADIX", text: radixErrorText(e, "Nie udało się usunąć połączeń.") }),
      },
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.pathBar}>
        <div className={styles.backButton}>
          <BackIcon onClick={onCanceled} />
        </div>
        {t("integrations.title")} <ArrowLeftIcon /> RADIX (WIP / PoGrun / GOK)
      </div>

      <Input label="Status" value={anyConnection ? "Aktywna" : "Brak połączeń"} onChange={() => {}} disabled />
      <Input label="WIP — upomnienia" value={state(settings.HasWip)} onChange={() => {}} disabled />
      <Input label="PoGrun — decyzje podatkowe" value={state(settings.HasPogrun)} onChange={() => {}} disabled />
      <Input label="GOK — zawiadomienia" value={state(settings.HasGok)} onChange={() => {}} disabled />
      <Input
        label="Miejscowości lokalne (doręczenie osobiste)"
        value={settings.Places.length > 0 ? settings.Places.join(", ") : "—"}
        onChange={() => {}}
        disabled
      />
      <div className={styles.hint}>
        Nota łączy się bezpośrednio z bazami MS SQL programów RADIX (serwer w sieci urzędu). Import paczek
        dokumentów jest w module RADIX na pasku bocznym; łańcuchy połączeń nie są wyświetlane po zapisie.
      </div>

      {!editing && (
        <div className={styles.actions}>
          <HollowButton text="Rozłącz" disabled={saving || !anyConnection || !settings.CanManage} onClick={disconnect} />
          <HollowButton text="Edytuj" disabled={!settings.CanManage} onClick={() => setEditing(true)} />
        </div>
      )}

      {editing && (
        <ConfigureRadix
          isUpdate
          toggle={() => setEditing(false)}
          onSuccess={() => {
            setEditing(false);
            onSaved();
          }}
        />
      )}
    </div>
  );
};

export default RadixSettings;
